import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, ThemeMode } from '../types';
import { fetchCurrentUser, logoutUser } from '../lib/api';
import { getStoredTheme, applyTheme, persistTheme } from '../lib/theme';
import { signRedirect } from '../lib/crypto';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  theme: ThemeMode;
  toggleTheme: () => Promise<void>;
  login: (targetPath?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [theme, setThemeState] = useState<ThemeMode>(getStoredTheme);

  // Initialize theme on mount
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const sync = (event: Event) => {
      const next = (event as CustomEvent<{ theme: string }>).detail.theme === 'dark' ? 'dark' : 'off-white';
      setThemeState(next);
      void persistTheme(next, Boolean(user));
    };
    window.addEventListener('nixlabs:theme', sync);
    return () => window.removeEventListener('nixlabs:theme', sync);
  }, [user]);

  // Live sync with session & Discord on reload
  const refreshUser = useCallback(async () => {
    try {
      const current = await fetchCurrentUser();
      setUser(current);
      if (current?.theme) {
        setThemeState(current.theme);
        applyTheme(current.theme);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const toggleTheme = async () => {
    const nextTheme: ThemeMode = theme === 'off-white' ? 'dark' : 'off-white';
    setThemeState(nextTheme);
    await persistTheme(nextTheme, Boolean(user));
  };

  const login = async (targetPath = '/triagem') => {
    const signed = await signRedirect(targetPath);
    window.location.href = `/api/auth/login?redirect=${signed}`;
  };

  const logout = async () => {
    await logoutUser();
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        theme,
        toggleTheme,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
